// Per-slide theme-world skins for SYSTEM slides (KULA-CREATE-LIBRARY-PLAN.md
// Phase 4, behind `slide_skins`). A skin changes the dress — backdrop, type,
// chrome, sound, feedback presentation — and never the mechanic, the author's
// words, scoring, or capture.
//
// This module is the WORLD-GRAMMAR registry: which worlds may honestly dress
// which slides. The rule comes from the flagship audit (handover §8.6): a
// world's feedback grammar must match the slide's learning contract — arcade
// win/lose celebration is honest on a genuinely gradeable task and a lie on a
// no-right-answer scale. The picker enforces the rule so instructors never
// learn it; refused combos are greyed with the reason.
//
// Pure module — no React, no network. Safe to import anywhere.

import type { SlideData } from '../components/course-builder/slides/slideTypes';
import {
  THEME_WORLD_IDS,
  type ThemeWorldId,
} from './scenario-builder/composition/themeWorlds.js';

/** The learning contract of a skinnable slide, as the grammar rule sees it. */
export type SlideCaptureClass =
  | 'gradeable' // has right answers the runtime scores (quiz, activity in quiz mode)
  | 'expressive' // the learner states a position with no right answer (survey, likert, ranking)
  | 'reflective'; // the learner writes for themselves (reflection prompts)

/**
 * Resolve a slide's capture class, or null when the slide is not skinnable
 * (v1 scope: quiz, activity, reflection — the system interactions. Static
 * content slides and Create experiences are out of scope; experiences carry
 * their own world by design).
 */
export function slideCaptureClass(data: SlideData): SlideCaptureClass | null {
  switch (data.template) {
    case 'quiz':
      return 'gradeable';
    case 'activity': {
      const mode = (data as { mode?: string }).mode;
      return mode === 'quiz' ? 'gradeable' : 'expressive';
    }
    case 'reflection':
      return 'reflective';
    default:
      return null;
  }
}

interface WorldGrammar {
  /** Capture classes this world's feedback grammar can honestly dress. */
  dresses: readonly SlideCaptureClass[];
  /** Plain-words refusal shown in the picker for classes it cannot. */
  refusalReason: string;
}

// Only Hyperpop carries win/lose machinery; every other world's register
// (revelation, examination, calm, growth, editorial) has no correctness
// celebration to lie with. Encoded per world so a future world with a
// scoring grammar declares itself here.
const WORLD_GRAMMARS: Record<ThemeWorldId, WorldGrammar> = {
  'neon-oracle': {
    dresses: ['gradeable', 'expressive', 'reflective'],
    refusalReason: '',
  },
  obsidian: {
    dresses: ['gradeable', 'expressive', 'reflective'],
    refusalReason: '',
  },
  aurora: {
    dresses: ['gradeable', 'expressive', 'reflective'],
    refusalReason: '',
  },
  terra: {
    dresses: ['gradeable', 'expressive', 'reflective'],
    refusalReason: '',
  },
  hyperpop: {
    dresses: ['gradeable'],
    refusalReason: 'Hyperpop celebrates right answers, and this slide doesn’t have one.',
  },
  risograph: {
    dresses: ['gradeable', 'expressive', 'reflective'],
    refusalReason: '',
  },
};

export interface SkinOption {
  world: ThemeWorldId;
  allowed: boolean;
  /** Present only when refused — one honest line for the picker. */
  reason?: string;
}

/**
 * The picker's option list for one slide: every world, each allowed or
 * refused-with-reason. Returns [] for non-skinnable slides (the picker
 * simply doesn't render).
 */
export function skinOptionsForSlide(data: SlideData): SkinOption[] {
  const cls = slideCaptureClass(data);
  if (!cls) return [];
  return THEME_WORLD_IDS.map((world) => {
    const grammar = WORLD_GRAMMARS[world];
    const allowed = grammar.dresses.includes(cls);
    return allowed ? { world, allowed } : { world, allowed, reason: grammar.refusalReason };
  });
}

/** True when `world` may dress `data` — the write-path guard mirroring the
 *  picker, so a stale/hand-edited override can never render dishonestly. */
export function canWorldDressSlide(world: ThemeWorldId, data: SlideData): boolean {
  const cls = slideCaptureClass(data);
  if (!cls) return false;
  return WORLD_GRAMMARS[world].dresses.includes(cls);
}
